import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { irA } from '../lib/dominios';
import {
  cambiarTipo, duplicarItem, nuevoItem, secciones,
} from '../lib/formularios';
import { useStore } from '../store/useStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { DriveSharePanel } from './DriveEditor';
import { OFFICE_APPS, ProductIcon } from '../components/office/icons';
import PestanaRespuestas from '../components/forms/respuestas/PestanaRespuestas';
import { ContextoEditor, ENCABEZADO, detalleError } from '../components/forms/editor/contexto';
import { fotoDe, opsDeFoto } from '../components/forms/editor/operaciones';
import useSincronizacion from '../components/forms/editor/useSincronizacion';
import useHistorial from '../components/forms/editor/useHistorial';
import EncabezadoEditor from '../components/forms/editor/EncabezadoEditor';
import EditorPreguntas from '../components/forms/editor/EditorPreguntas';
import PestanaConfiguracion from '../components/forms/editor/PestanaConfiguracion';
import PanelTema from '../components/forms/editor/PanelTema';
import DialogoPublicar from '../components/forms/editor/DialogoPublicar';
import DialogoEnviar from '../components/forms/editor/DialogoEnviar';
import DialogoImportar from '../components/forms/editor/DialogoImportar';
import PanelAurora from '../components/forms/editor/PanelAurora';
import BarraAurora from '../components/forms/editor/BarraAurora';
import DialogoPreferencias from '../components/forms/editor/DialogoPreferencias';
import PestanaCasos from '../components/forms/buzon/PestanaCasos';
import ConfigBuzon from '../components/forms/buzon/ConfigBuzon';
import { BUZON_CASOS } from '../lib/modulos';
import '../components/forms/forms.css';
import '../components/forms/editor/editor.css';

const PESTANAS = BUZON_CASOS ? ['preguntas', 'respuestas', 'casos', 'configuracion'] : ['preguntas', 'respuestas', 'configuracion'];
// Tema, ajustes y el nombre del archivo no entran en deshacer: Google tampoco los deshace con Ctrl+Z
const SIN_HISTORIAL = new Set(['tema', 'ajustes', 'nombre']);
const MAX_IMAGEN = 5 * 1024 * 1024;

const esCampoDeTexto = (el) => !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

/**
 * Editor de Orvian Forms (https://docs.orvian.mx/form/<id>): Preguntas, Respuestas y Configuración.
 * Contrato del formulario y de la API: email/claude/formularios.md.
 */
export default function FormEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const movil = useIsMobile(900);
  const usuario = useStore((s) => s.user);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const oscuro = theme === 'dark';

  const {
    datos, setDatos, cargando, errorCarga, guardado, aplicar, parchear, recargar, setFoco,
  } = useSincronizacion(id);
  const {
    registrar, deshacer: fotoAnterior, rehacer: fotoSiguiente, puedeDeshacer, puedeRehacer,
  } = useHistorial();

  const formulario = datos?.formulario;
  const archivo = datos?.archivo;
  const puedeEditar = !!datos?.puede_editar;

  const [activo, setActivoEstado] = useState(ENCABEZADO);
  const [nuevoFoco, setNuevoFoco] = useState(null);
  const [temaAbierto, setTemaAbierto] = useState(false);
  const [auroraAbierta, setAuroraAbierta] = useState(false);
  const [dialogo, setDialogo] = useState(null); // publicar | enviar | importar | aurora | preferencias | colaboradores | buzon

  const pestanaUrl = params.get('pestana');
  // «Casos» (buzón de quejas y sugerencias): quien edita la ve siempre (para activarlo); el resto, si está activo
  const verCasos = BUZON_CASOS && (puedeEditar || !!datos?.formulario?.buzon?.activo);
  const pestana = PESTANAS.includes(pestanaUrl) && (pestanaUrl !== 'respuestas' || puedeEditar || !datos)
    && (pestanaUrl !== 'casos' || verCasos || !datos)
    ? pestanaUrl : 'preguntas';

  // Referencias para que las acciones (estables) lean siempre lo último sin recrearse en cada tecla
  const formRef = useRef(formulario);
  formRef.current = formulario;
  const puedeRef = useRef(puedeEditar);
  puedeRef.current = puedeEditar;
  const activoRef = useRef(activo);
  activoRef.current = activo;

  useEffect(() => {
    if (archivo?.name) document.title = `${archivo.name} · ${OFFICE_APPS.form.name}`;
  }, [archivo?.name]);

  const cambiarPestana = useCallback((p) => {
    const nuevos = new URLSearchParams(params);
    if (p === 'preguntas') nuevos.delete('pestana');
    else nuevos.set('pestana', p);
    setParams(nuevos, { replace: true });
  }, [params, setParams]);

  const setActivo = useCallback((valor) => {
    setActivoEstado(valor);
    setFoco(valor === ENCABEZADO ? null : valor);
  }, [setFoco]);

  /* ------------------------------------------------------------ edición */

  const editar = useCallback((ops, { clave, inmediato = false, historial = true } = {}) => {
    if (!puedeRef.current || !formRef.current) return;
    if (historial) registrar(fotoDe(formRef.current), clave);
    aplicar(ops, { inmediato });
  }, [aplicar, registrar]);

  const cambiarItem = useCallback((itemId, campos, { clave } = {}) => {
    editar([{ op: 'item_set', id: itemId, campos }], { clave: clave || `${itemId}:${Object.keys(campos).join(',')}` });
  }, [editar]);

  const setCampo = useCallback((ruta, valor, { clave, historial } = {}) => {
    editar([{ op: 'set', ruta, valor }], {
      clave: clave || `form:${ruta}`,
      historial: historial ?? !SIN_HISTORIAL.has(ruta),
    });
  }, [editar]);

  const irAItem = (itemId) => {
    requestAnimationFrame(() => {
      document.querySelector(`[data-item="${itemId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  const agregarItems = useCallback((nuevos) => {
    if (!nuevos?.length) return;
    const items = formRef.current?.items || [];
    const act = activoRef.current;
    // Lo nuevo va justo después de la tarjeta activa; con el encabezado activo, al principio
    let despues = null;
    if (act && act !== ENCABEZADO) despues = items.some((it) => it.id === act) ? act : (items[items.length - 1]?.id || null);
    const ops = nuevos.map((item) => {
      const op = { op: 'item_add', item, despues_de: despues };
      despues = item.id;
      return op;
    });
    editar(ops, { inmediato: true });
    const ultimo = nuevos[nuevos.length - 1];
    setActivo(ultimo.id);
    setNuevoFoco(ultimo.id);
    irAItem(ultimo.id);
  }, [editar, setActivo]);

  const valoresNuevos = () => {
    const a = formRef.current?.ajustes || {};
    return {
      obligatoria: !!a.obligatorias_por_defecto,
      puntos: a.es_cuestionario ? (a.cuestionario?.puntos_por_defecto || 0) : 0,
    };
  };

  const subirImagen = useCallback(() => new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/gif,image/webp';
    input.onchange = async () => {
      const archivoImg = input.files?.[0];
      if (!archivoImg) {
        resolve(null);
        return;
      }
      if (archivoImg.size > MAX_IMAGEN) {
        toast.error('La imagen supera 5 MB');
        resolve(null);
        return;
      }
      const aviso = toast.loading('Subiendo imagen…');
      try {
        const fd = new FormData();
        fd.append('archivo', archivoImg);
        const { data } = await api.post(`/forms/${id}/imagenes`, fd);
        toast.dismiss(aviso);
        resolve({ id: data.id });
      } catch (err) {
        toast.dismiss(aviso);
        toast.error(detalleError(err, 'No se pudo subir la imagen'));
        resolve(null);
      }
    };
    input.click();
  }), [id]);

  const acciones = {
    cambiarItem,
    setCampo,
    subirImagen,
    agregarItems,
    agregarNuevo: (tipo) => agregarItems([nuevoItem(tipo, valoresNuevos())]),
    agregarImagen: async () => {
      const img = await subirImagen();
      if (img) agregarItems([{ ...nuevoItem('imagen'), imagen: img }]);
    },
    duplicar: (itemId) => {
      const item = formRef.current?.items.find((it) => it.id === itemId);
      if (!item) return;
      const copia = duplicarItem(item);
      editar([{ op: 'item_add', item: copia, despues_de: itemId }], { inmediato: true });
      setActivo(copia.id);
      irAItem(copia.id);
    },
    borrarItem: (itemId) => {
      const items = formRef.current?.items || [];
      const i = items.findIndex((it) => it.id === itemId);
      editar([{ op: 'item_del', id: itemId }], { inmediato: true });
      setActivo(i > 0 ? items[i - 1].id : ENCABEZADO);
    },
    moverItem: (itemId, despuesDe) => editar([{ op: 'item_move', id: itemId, despues_de: despuesDe }], { inmediato: true }),
    moverItemPasos: (itemId, delta) => {
      const items = formRef.current?.items || [];
      const i = items.findIndex((it) => it.id === itemId);
      if (i < 0) return;
      let despues;
      if (delta < 0) {
        if (i === 0) return;
        despues = i >= 2 ? items[i - 2].id : null;
      } else {
        if (i === items.length - 1) return;
        despues = items[i + 1].id;
      }
      editar([{ op: 'item_move', id: itemId, despues_de: despues }], { inmediato: true });
      irAItem(itemId);
    },
    cambiarTipoItem: (itemId, tipo) => {
      const item = formRef.current?.items.find((it) => it.id === itemId);
      if (item) editar([{ op: 'item_set', id: itemId, campos: cambiarTipo(item, tipo) }], { inmediato: true });
    },
    duplicarSeccion: (seccionId) => {
      const bloque = secciones(formRef.current?.items).find((s) => s.id === seccionId);
      if (!bloque?.item) return;
      const copias = [bloque.item, ...bloque.items].map(duplicarItem);
      let despues = bloque.items.length ? bloque.items[bloque.items.length - 1].id : bloque.item.id;
      const ops = copias.map((item) => {
        const op = { op: 'item_add', item, despues_de: despues };
        despues = item.id;
        return op;
      });
      editar(ops, { inmediato: true });
      setActivo(copias[0].id);
      irAItem(copias[0].id);
    },
    moverSeccion: (seccionId, delta) => {
      const bloques = secciones(formRef.current?.items);
      const i = bloques.findIndex((s) => s.id === seccionId);
      const j = i + delta;
      // La primera sección empieza en el encabezado del formulario: esa no se mueve ni se salta
      if (i < 1 || j < 1 || j >= bloques.length) return;
      [bloques[i], bloques[j]] = [bloques[j], bloques[i]];
      const items = bloques.flatMap((s) => (s.item ? [s.item, ...s.items] : s.items));
      editar([{ op: 'items', items }], { inmediato: true });
      irAItem(seccionId);
    },
    borrarSeccion: (seccionId, { conPreguntas }) => {
      const bloque = secciones(formRef.current?.items).find((s) => s.id === seccionId);
      if (!bloque?.item) return;
      if (conPreguntas) {
        const ids = new Set([bloque.item.id, ...bloque.items.map((it) => it.id)]);
        const items = formRef.current.items.filter((it) => !ids.has(it.id));
        editar([{ op: 'items', items }], { inmediato: true });
        toast.message('Sección eliminada', {
          description: `${bloque.items.length} ${bloque.items.length === 1 ? 'elemento' : 'elementos'} con ella.`,
          action: { label: 'Deshacer', onClick: () => deshacer() },
        });
      } else {
        editar([{ op: 'item_del', id: seccionId }], { inmediato: true });
      }
      setActivo(ENCABEZADO);
    },
    abrirDialogo: (nombre) => setDialogo(nombre),
  };

  /* ------------------------------------------------------------ deshacer */

  const deshacer = useCallback(() => {
    const f = formRef.current;
    if (!f || !puedeRef.current) return;
    const foto = fotoAnterior(fotoDe(f));
    if (foto) aplicar(opsDeFoto(foto), { inmediato: true });
  }, [aplicar, fotoAnterior]);

  const rehacer = useCallback(() => {
    const f = formRef.current;
    if (!f || !puedeRef.current) return;
    const foto = fotoSiguiente(fotoDe(f));
    if (foto) aplicar(opsDeFoto(foto), { inmediato: true });
  }, [aplicar, fotoSiguiente]);

  useEffect(() => {
    const alTeclear = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const tecla = e.key.toLowerCase();
      if (tecla !== 'z' && tecla !== 'y') return;
      // Dentro de un campo manda el deshacer del propio campo (el del navegador)
      if (esCampoDeTexto(document.activeElement)) return;
      e.preventDefault();
      if (tecla === 'y' || e.shiftKey) rehacer();
      else deshacer();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [deshacer, rehacer]);

  /* ------------------------------------------------------------ publicación y avisos */

  const onPublicacion = useCallback(async (parcial) => {
    const { data } = await api.put(`/forms/${id}/publicacion`, parcial);
    parchear((f) => ({ ...f, publicacion: { ...(f.publicacion || {}), ...(data.publicacion || {}) } }));
    return data.publicacion;
  }, [id, parchear]);

  const onAvisos = useCallback(async (activos) => {
    const { data } = await api.put(`/forms/${id}/avisos`, { activos });
    setDatos((d) => d && { ...d, avisos: !!data.activos });
    return data.activos;
  }, [id, setDatos]);

  const onRecargar = useCallback(() => {
    recargar({ silencioso: true });
  }, [recargar]);

  /* ------------------------------------------------------------ cabecera */

  const accionesCabecera = {
    onNombre: (nombre) => editar([{ op: 'set', ruta: 'nombre', valor: nombre }], { inmediato: true, historial: false }),
    onTema: () => {
      if (pestana !== 'preguntas') cambiarPestana('preguntas');
      setTemaAbierto((v) => !v);
    },
    onVistaPrevia: () => window.open(`/form/${id}/preview`, '_blank', 'noopener'),
    onPrecompletar: () => window.open(`/form/${id}/preview?precompletar=1`, '_blank', 'noopener'),
    onDeshacer: deshacer,
    onRehacer: rehacer,
    onPublicar: () => setDialogo('publicar'),
    onEnviar: () => setDialogo('enviar'),
    // Aurora IA: el botón de arriba abre el panel lateral; para cambiar el formulario está la barra de abajo
    onAurora: () => setAuroraAbierta((v) => !v),
    onColaboradores: () => setDialogo('colaboradores'),
    onPreferencias: () => setDialogo('preferencias'),
    onCopiar: async () => {
      const aviso = toast.loading('Haciendo una copia…');
      try {
        const { data } = await api.post(`/forms/${id}/copiar`, { nombre: `Copia de ${archivo?.name || 'formulario'}` });
        toast.dismiss(aviso);
        toast.success('Copia creada en tu Drive');
        window.open(`/form/${data.archivo.id}`, '_blank', 'noopener');
      } catch (err) {
        toast.dismiss(aviso);
        toast.error(detalleError(err, 'No se pudo hacer la copia'));
      }
    },
    onPapelera: datos?.mi_rol === 'owner' ? async () => {
      try {
        await api.delete(`/drive/files/${id}`);
        toast.success('Formulario movido a la papelera');
        irA(nav, '/drive');
      } catch (err) {
        toast.error(detalleError(err, 'No se pudo mover a la papelera'));
      }
    } : null,
    onImprimir: () => {
      if (pestana !== 'preguntas') cambiarPestana('preguntas');
      setActivo(ENCABEZADO);
      setTimeout(() => window.print(), 250);
    },
    onDrive: () => irA(nav, '/drive'),
    onCorreo: () => irA(nav, '/mail'),
    onAlternarOscuro: () => setTheme(oscuro ? 'light' : 'dark'),
  };

  /* ------------------------------------------------------------ render */

  if (cargando && !datos) {
    return (
      <div className="fe-pantalla-centro">
        <ProductIcon kind="form" size={52} />
        <Loader2 className="od-spin" />
      </div>
    );
  }

  if (errorCarga || !formulario) {
    const sinAcceso = errorCarga?.status === 403 || errorCarga?.status === 404;
    return (
      <div className="fe-pantalla-centro">
        <ProductIcon kind="form" size={52} />
        <h1>{sinAcceso ? 'No puedes abrir este formulario' : 'No se pudo abrir el formulario'}</h1>
        <p>{sinAcceso ? 'Puede que se haya eliminado o que no lo hayan compartido contigo.' : errorCarga?.mensaje}</p>
        <div className="fe-fila-controles">
          {!sinAcceso && <button type="button" className="fe-btn-borde" onClick={() => recargar()}>Reintentar</button>}
          <button type="button" className="fe-btn-primario" onClick={() => irA(nav, '/drive')}>Ir a Drive</button>
        </div>
      </div>
    );
  }

  const contexto = {
    formId: id,
    formulario,
    archivo,
    puedeEditar,
    oscuro,
    activo,
    setActivo,
    nuevoFoco,
    limpiarNuevoFoco: () => setNuevoFoco(null),
    esCuestionario: !!formulario.ajustes?.es_cuestionario,
    onPublicacion,
    onBuzon: () => setDialogo('buzon'),
    ...acciones,
  };

  return (
    <ContextoEditor.Provider value={contexto}>
      <div className={`fe-raiz ${oscuro ? 'is-oscuro' : ''}`} data-testid="form-editor">
        <EncabezadoEditor
          archivo={archivo}
          formulario={formulario}
          puedeEditar={puedeEditar}
          guardado={guardado}
          presencia={datos.presencia}
          usuario={usuario}
          oscuro={oscuro}
          movil={movil}
          pestana={pestana}
          onPestana={cambiarPestana}
          acciones={accionesCabecera}
          puedeDeshacer={puedeDeshacer}
          puedeRehacer={puedeRehacer}
          auroraAbierta={auroraAbierta}
        />

        <div className="fe-cuerpo">
        <div className="fe-escenario">
        <main className="fe-contenido fm-scroll">
          {pestana === 'preguntas' && <EditorPreguntas />}
          {pestana === 'respuestas' && puedeEditar && (
            <div className="fe-pestana-respuestas">
              <PestanaRespuestas
                formId={id}
                formulario={formulario}
                puedeEditar={puedeEditar}
                avisos={!!datos.avisos}
                onAvisos={onAvisos}
                onPublicacion={onPublicacion}
                onRecargar={onRecargar}
              />
            </div>
          )}
          {pestana === 'casos' && (
            <PestanaCasos
              formId={id}
              formulario={formulario}
              puedeEditar={puedeEditar}
              onConfigurar={() => setDialogo('buzon')}
              onActivado={(buzon) => parchear((f) => ({ ...f, buzon }))}
            />
          )}
          {pestana === 'configuracion' && <PestanaConfiguracion />}
        </main>

        {/* La barra de Aurora IA vive sobre el lienzo de preguntas, como en Orvian Docs */}
        {pestana === 'preguntas' && puedeEditar && <BarraAurora formId={id} movil={movil} />}
        </div>

        {temaAbierto && pestana === 'preguntas' && (
          <PanelTema
            tema={formulario.tema}
            onCambiar={(tema) => setCampo('tema', tema, { clave: 'form:tema' })}
            onCerrar={() => setTemaAbierto(false)}
            subirImagen={subirImagen}
            deshabilitado={!puedeEditar}
          />
        )}

        {auroraAbierta && (
          <PanelAurora
            formId={id}
            titulo={formulario.titulo}
            nombreUsuario={usuario?.name || usuario?.email}
            onCerrar={() => setAuroraAbierta(false)}
          />
        )}
        </div>

        <DialogoPublicar
          open={dialogo === 'publicar'}
          onOpenChange={(v) => setDialogo(v ? 'publicar' : null)}
          formulario={formulario}
          onPublicacion={onPublicacion}
          correoUsuario={usuario?.email}
        />
        <DialogoEnviar
          open={dialogo === 'enviar'}
          onOpenChange={(v) => setDialogo(v ? 'enviar' : null)}
          formId={id}
          formulario={formulario}
          onPublicar={() => setDialogo('publicar')}
        />
        <DialogoImportar
          open={dialogo === 'importar'}
          onOpenChange={(v) => setDialogo(v ? 'importar' : null)}
          formId={id}
          onImportar={agregarItems}
        />
        <DialogoPreferencias
          open={dialogo === 'preferencias'}
          onOpenChange={(v) => setDialogo(v ? 'preferencias' : null)}
          ajustes={formulario.ajustes}
          onGuardar={(ajustes) => setCampo('ajustes', ajustes)}
        />
        {puedeEditar && (
          <ConfigBuzon
            formId={id}
            open={dialogo === 'buzon'}
            onOpenChange={(v) => setDialogo(v ? 'buzon' : null)}
            onGuardado={(buzon) => parchear((f) => ({ ...f, buzon }))}
          />
        )}
        {archivo && (
          <DriveSharePanel
            open={dialogo === 'colaboradores'}
            onOpenChange={(v) => setDialogo(v ? 'colaboradores' : null)}
            fileId={id}
            fileName={archivo.name}
          />
        )}
      </div>
    </ContextoEditor.Provider>
  );
}
